export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const detectPlatform = (text) => {
  try {
    const u = new URL(text.trim());
    if (!["http:", "https:"].includes(u.protocol)) return null;
    const h = u.hostname.toLowerCase();
    if (h === "instagram.com" || h.endsWith(".instagram.com")) return "instagram";
    if (h === "tiktok.com" || h.endsWith(".tiktok.com")) return "tiktok";
  } catch {
    return null;
  }
  return null;
};

export const extractLinks = (text) =>
  [...new Set((text.match(/https?:\/\/[^\s,]+/g) || []).filter(detectPlatform))];

export const thumbUrl = (u) => (!u ? "" : u.startsWith("data:") || u.startsWith(API) ? u : `${API}/thumb?u=${encodeURIComponent(u)}`);
export const coverUrl = (id) => `${API}/covers/${id}`;

export const resLabel = (meta) => {
  const p = Math.min(meta?.width || 0, meta?.height || 0);
  if (!p) return "";
  return p >= 2160 ? "4K" : p >= 1440 ? "1440p" : p >= 1080 ? "1080p" : `${p}p`;
};

const PHASES = {
  queued: "Starting", fetching: "Finding video", downloading: "Downloading", queued_upscale: "Waiting for upscaler",
  upscaling: "Upscaling to 4K", finishing: "Finishing", saving: "Saving to device", ready: "Done",
};

export const progressText = (p) => {
  if (!p) return "";
  const pct = p.phase === "fetching" || p.phase === "queued" ? "" : ` ${Math.round((p.progress || 0) * 100)}%`;
  const eta = p.eta > 0.5 ? ` · ${p.eta >= 60 ? `${Math.floor(p.eta / 60)}m ${Math.round(p.eta % 60)}s` : `${Math.ceil(p.eta)}s`} left` : "";
  return `${PHASES[p.phase] || "Working"}${pct}${eta}`;
};

export async function apiFetch(path, { token, ...opts } = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    let msg = `Server error ${res.status}`;
    try {
      msg = (await res.json()).detail || msg;
    } catch {}
    throw new Error(msg);
  }
  return res;
}

const SERVER_SHARE = 0.92;
const scaled = (job) => ({ ...job, progress: (job.progress || 0) * SERVER_SHARE });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function readWithProgress(res, onProgress) {
  const total = Number(res.headers.get("Content-Length")) || 0;
  if (!res.body || !total) return res.blob();
  const reader = res.body.getReader();
  const chunks = [];
  let got = 0;
  const t0 = Date.now();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    got += value.length;
    const rate = got / Math.max((Date.now() - t0) / 1000, 0.1);
    onProgress({ phase: "saving", progress: SERVER_SHARE + (1 - SERVER_SHARE) * (got / total), eta: (total - got) / rate });
  }
  return new Blob(chunks, { type: "video/mp4" });
}

// Starts a server job, polls its progress, then streams the finished MP4 to the browser.
export async function fetchReel(url, { quality = "original", token, onProgress = () => {} } = {}) {
  let job = await (await apiFetch("/jobs", { method: "POST", token, body: JSON.stringify({ url: url.trim(), quality }) })).json();
  onProgress(scaled(job));
  while (job.state === "running") {
    await sleep(800);
    job = await (await apiFetch(`/jobs/${job.id}`)).json();
    onProgress(scaled(job));
  }
  if (job.state === "error") throw new Error(job.error);
  const res = await apiFetch(`/jobs/${job.id}/file`);
  const blob = await readWithProgress(res, onProgress);
  onProgress({ phase: "ready", progress: 1, eta: 0 });
  return { blob, name: job.filename, meta: job.meta || {} };
}

export const saveBlob = (blob, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);
};

export const canShareFiles = (files) =>
  typeof navigator !== "undefined" && !!navigator.canShare && navigator.canShare({ files });

export async function shareFiles(items) {
  const files = items.map((i) => new File([i.blob], i.name, { type: "video/mp4" }));
  if (!canShareFiles(files)) return false;
  try {
    await navigator.share({ files });
  } catch (e) {
    if (e.name !== "AbortError") throw e;
  }
  return true;
}

export async function zipAll(items) {
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  const seen = {};
  items.forEach((i) => {
    const n = (seen[i.name] = (seen[i.name] || 0) + 1);
    zip.file(n > 1 ? i.name.replace(/\.mp4$/, `_${n}.mp4`) : i.name, i.blob);
  });
  const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
  saveBlob(blob, `RecipeRemedy_${items.length}_recipes.zip`);
}
